import { useEffect, useState } from "react";
import { Loader2, Sparkles, Search, FileDown, Mail, Activity, Home } from "lucide-react";
import { toast } from "sonner";
import { pubApi } from "./clienteApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BehaviorAnalysis } from "@/components/monitoring/BehaviorAnalysis";
import { ConsumptionAnalysis } from "@/components/monitoring/ConsumptionAnalysis";

const GRAV = { bassa: "#22c55e", media: "#eab308", alta: "#ef4444" };
const dtt = (s) => (s ? new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "");
const BTN = "inline-flex items-center gap-1 rounded-full border border-neutral-700 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-neutral-200 transition-colors hover:border-[#C17F59] hover:text-[#C17F59] disabled:opacity-50";

// Popup allarme in stile Torri Monitoring per il cliente Versione Pro (solo Diagnosi + Cause probabili).
export function ProAlarmDialog({ src, alarm, onClose, accent = "#C17F59" }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [mailOpen, setMailOpen] = useState(false);
  const [to, setTo] = useState("");
  const [sending, setSending] = useState(false);
  useEffect(() => {
    if (!alarm) return;
    setD(null); setErr(""); setMailOpen(false); setTo("");
    pubApi.post(`/cliente/allarmi/${encodeURIComponent(alarm.code)}/analisi`, null, { headers: src.headers, timeout: 85000 })
      .then((r) => setD(r.data)).catch((e) => setErr(e.response?.data?.detail || "Analisi non disponibile, riprova tra poco"));
  }, [alarm]); // eslint-disable-line react-hooks/exhaustive-deps
  const pdf = async () => {
    setBusy(true);
    const w = window.open("", "_blank");
    try {
      const r = await pubApi.get(`/cliente/allarmi/${encodeURIComponent(alarm.code)}/relazione.pdf`, { headers: src.headers, responseType: "blob", timeout: 120000 });
      const u = URL.createObjectURL(r.data);
      if (w) w.location.href = u; else window.location.href = u;
    } catch { w?.close(); toast.error("PDF non disponibile"); }
    setBusy(false);
  };
  const invia = async () => {
    const dest = to.split(/[,;]/).map((x) => x.trim()).filter((x) => x.includes("@"));
    if (!dest.length) { toast.error("Inserisci un indirizzo email valido"); return; }
    setSending(true);
    try {
      await pubApi.post(`/cliente/allarmi/${encodeURIComponent(alarm.code)}/relazione/email`, { to: dest, subject: d?.titolo ? `Relazione tecnica · ${d.titolo}` : undefined }, { headers: src.headers, timeout: 120000 });
      toast.success("Email inviata con la relazione in allegato"); setMailOpen(false); setTo("");
    } catch (e) { toast.error(e.response?.data?.detail || "Invio non riuscito, riprova"); }
    finally { setSending(false); }
  };
  const meta = d && [d.model, d.sn && `SN ${d.sn}`, d.occorrenze && `${d.occorrenze} volte`, d.at && `ultimo ${dtt(d.at)}`].filter(Boolean).join(" · ");
  return (
    <Dialog open={!!alarm} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto border-neutral-800 bg-neutral-950 text-white" data-testid="cliente-pro-alarm-dialog" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-lg" style={{ color: accent }}>
            <Sparkles className="h-5 w-5" /><span data-testid="cliente-pro-alarm-title">{d?.titolo || alarm?.code}</span>
          </DialogTitle>
          {d && (
            <div className="flex flex-wrap gap-2" data-testid="cliente-pro-alarm-actions">
              <button type="button" onClick={pdf} disabled={busy} className={BTN} data-testid="cliente-pro-alarm-pdf">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}PDF</button>
              <button type="button" onClick={() => setMailOpen((v) => !v)} className={BTN} data-testid="cliente-pro-alarm-email"><Mail className="h-3.5 w-3.5" />Email</button>
            </div>
          )}
          {d && meta && <p className="text-xs text-neutral-400" data-testid="cliente-pro-alarm-meta">{meta}</p>}
        </DialogHeader>
        {mailOpen && (
          <div className="rounded-xl border border-neutral-800 bg-black/40 p-3" data-testid="cliente-pro-alarm-mailform">
            <label className="text-[11px] uppercase tracking-widest text-neutral-400">Invia la relazione a</label>
            <input value={to} onChange={(e) => setTo(e.target.value)} placeholder="email@esempio.it" data-testid="cliente-pro-alarm-to"
              className="mt-1 w-full rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-white outline-none focus:border-[#C17F59]" />
            <div className="mt-2 flex justify-end gap-2">
              <button type="button" onClick={() => setMailOpen(false)} className="rounded-full px-3 py-1 text-xs text-neutral-400">Annulla</button>
              <button type="button" onClick={invia} disabled={sending} data-testid="cliente-pro-alarm-send"
                className="inline-flex items-center gap-1 rounded-full bg-[#C17F59] px-3 py-1 text-xs font-bold text-white disabled:opacity-50">{sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}Invia</button>
            </div>
          </div>
        )}
        {err ? <p className="py-6 text-sm text-red-400" data-testid="cliente-pro-alarm-error">{err}</p>
          : !d ? <div className="flex items-center gap-2 py-6 text-sm text-neutral-400" data-testid="cliente-pro-alarm-loading"><Loader2 className="h-5 w-5 animate-spin" style={{ color: accent }} />Analisi AI in corso…</div>
            : (
              <div className="space-y-5" data-testid="cliente-pro-alarm-body">
                {d.manuali_avviso && <p className="rounded-lg border border-red-500/40 bg-red-500/10 px-3 py-2 text-xs text-red-300">{d.manuali_avviso}: l'analisi usa l'ultima versione dei manuali disponibile.</p>}
                <span className="inline-block rounded-full border px-3 py-0.5 text-[10px] font-bold uppercase" style={{ borderColor: GRAV[d.gravita], color: GRAV[d.gravita] }} data-testid="cliente-pro-alarm-gravita">Gravità {d.gravita}</span>
                <div className="space-y-1.5" data-testid="cliente-pro-alarm-diagnosi">
                  <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-neutral-400"><Search className="h-3.5 w-3.5" />Diagnosi</div>
                  <p className="text-sm leading-relaxed text-neutral-200">{d.diagnosi}</p>
                </div>
                {!!d.cause_probabili?.length && (
                  <div className="space-y-1.5" data-testid="cliente-pro-alarm-cause">
                    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-neutral-400"><Search className="h-3.5 w-3.5" />Cause probabili</div>
                    <ul className="list-disc space-y-1 pl-5 text-sm text-neutral-200">{d.cause_probabili.map((c, i) => <li key={i}>{c}</li>)}</ul>
                  </div>
                )}
                <p className="text-[11px] text-neutral-600">Analisi generata con AI il {dtt(d.generata_il)} su dati cloud FIER e manuali Torri: verificare sempre in campo.</p>
              </div>
            )}
      </DialogContent>
    </Dialog>
  );
}

// I due pulsanti di analisi (comportamento/batteria e consumi) con PDF scaricabile, per la Vista Cliente Pro.
export function AnalisiProButtons({ src, accent = "#C17F59" }) {
  const [beh, setBeh] = useState(false);
  const [cons, setCons] = useState(false);
  const extra = { headers: src.headers };
  const dl = async (path) => {
    const w = window.open("", "_blank");
    try {
      const r = await pubApi.get(path, { headers: src.headers, responseType: "blob", timeout: 120000 });
      const u = URL.createObjectURL(r.data);
      if (w) w.location.href = u; else window.location.href = u;
    } catch { w?.close(); toast.error("PDF non disponibile: apri prima l'analisi"); }
  };
  return (
    <div className="space-y-2.5" data-testid="cliente-analisi-pro">
      <button type="button" onClick={() => setBeh(true)} data-testid="cliente-comportamento-btn"
        className="flex w-full items-center justify-center gap-2 rounded-2xl border border-[#C17F59]/40 bg-[#FBF3EE] px-4 py-3 text-sm font-bold text-[#9d6a4a] transition-transform active:scale-[0.98]">
        <Activity className="h-4 w-4 shrink-0" /><span>Analizza comportamento e batteria <span className="whitespace-nowrap">(anno vs anni precedenti)</span></span></button>
      <button type="button" onClick={() => setCons(true)} data-testid="cliente-consumi-btn"
        className="flex w-full items-center justify-center gap-2 rounded-2xl border border-sky-300 bg-sky-50 px-4 py-3 text-sm font-bold text-sky-700 transition-transform active:scale-[0.98]">
        <Home className="h-4 w-4" />Analizza consumi dell'utenza</button>
      <BehaviorAnalysis open={beh} onClose={() => setBeh(false)} accent={accent} url="/cliente/comportamento" client={pubApi} extra={extra} pdf={() => dl("/cliente/comportamento/relazione.pdf")} />
      <ConsumptionAnalysis open={cons} onClose={() => setCons(false)} accent={accent} url="/cliente/consumi-utenza" client={pubApi} extra={extra} pdf={() => dl("/cliente/consumi-utenza/relazione.pdf")} />
    </div>
  );
}
